import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";

const STORAGE_KEY = "vyv_strava_oauth_state";

type Status = "loading" | "success" | "error";

interface AthleteInfo {
  id: number;
  firstname: string | null;
  lastname: string | null;
}

export default function StravaCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [status, setStatus] = useState<Status>("loading");
  const [athlete, setAthlete] = useState<AthleteInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const handleCallback = async () => {
      try {
        const code = searchParams.get("code");
        const state = searchParams.get("state");
        const errorParam = searchParams.get("error");

        if (errorParam) {
          if (!cancelled) {
            setStatus("error");
            setErrorMessage(t("strava.callback.userDenied"));
          }
          return;
        }

        if (!code || !state) {
          if (!cancelled) {
            setStatus("error");
            setErrorMessage(t("strava.callback.missingParams"));
          }
          return;
        }

        const expectedState = sessionStorage.getItem(STORAGE_KEY);
        if (!expectedState) {
          if (!cancelled) {
            setStatus("error");
            setErrorMessage(t("strava.callback.stateMissing"));
          }
          return;
        }

        if (state !== expectedState) {
          if (!cancelled) {
            setStatus("error");
            setErrorMessage(t("strava.callback.stateMismatch"));
          }
          return;
        }

        sessionStorage.removeItem(STORAGE_KEY);

        // O Strava devolve o escopo REALMENTE concedido no query param `scope`
        // (ex.: "read,activity:read_all"). A tela de consent tem checkboxes que
        // o usuário pode desmarcar — sem encaminhar isso, o callback gravava o
        // escopo PEDIDO como se fosse o concedido, e um grant sem
        // activity:read* virava "Page 1: 403" eterno, indiagnosticável pelo
        // banco. Caso real em produção: a única conexão ativa, partial há dias.
        const grantedScope = searchParams.get("scope");

        const { data, error } = await supabase.functions.invoke("strava-oauth-callback", {
          body: { code, state, expectedState, scope: grantedScope },
        });

        if (cancelled) return;

        if (error || !data?.success) {
          console.error("[StravaCallback] Edge function error:", error, data);
          setStatus("error");
          // insufficient_scope tem mensagem própria: o usuário desmarcou a
          // permissão de atividades e precisa reconectar concedendo — dizer
          // "falhou" genérico mandaria ele repetir o mesmo gesto.
          const detail = (data as { error?: string } | null)?.error;
          setErrorMessage(
            detail === "insufficient_scope"
              ? t("strava.callback.insufficientScope")
              : detail || t("strava.callback.exchangeFailed"),
          );
          return;
        }

        setAthlete(data.athlete);
        setStatus("success");

        supabase.functions.invoke("strava-sync", { body: {} }).catch((err) => {
          console.error("[StravaCallback] Initial sync failed (non-fatal):", err);
        });

        setTimeout(() => {
          if (!cancelled) navigate("/settings/connections");
        }, 2500);
      } catch (err) {
        if (cancelled) return;
        console.error("[StravaCallback] Unexpected error:", err);
        setStatus("error");
        setErrorMessage(t("strava.callback.unexpectedError"));
      }
    };

    handleCallback();
    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, t]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full text-center space-y-6"
      >
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 mx-auto animate-spin text-primary" />
            <h1 className="text-2xl font-display font-medium">
              {t("strava.callback.connecting")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t("strava.callback.connectingDesc")}
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-500" />
            <h1 className="text-2xl font-display font-medium">
              {t("strava.callback.connected")}
            </h1>
            {athlete && (
              <p className="text-muted-foreground">
                {t("strava.callback.connectedAs")}{" "}
                <span className="text-foreground font-medium">
                  {[athlete.firstname, athlete.lastname].filter(Boolean).join(" ") ||
                    `Athlete ${athlete.id}`}
                </span>
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {t("strava.callback.redirecting")}
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-destructive" />
            <h1 className="text-2xl font-display font-medium">
              {t("strava.callback.errorTitle")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {errorMessage || t("strava.callback.unexpectedError")}
            </p>
            <button
              onClick={() => navigate("/settings/connections")}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium"
            >
              {t("strava.callback.backToSettings")}
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
